import {useMutation, useQueryClient} from '@tanstack/react-query';

import {DecisionTreeModel} from '../../../../../libs/shared/core/src/models/api/decisionTreeModel';
import {FetchSubtreesResult, subtreeQueryKeyFactory} from '../../../../settings/fetchers/decisionTrees';

/*
 * Hooks.
 */

/**
 *  Hook that allows you to update the subtrees react-query cache when updating or deleting an individual subtree.
 *  This allows us to see updates to the subtree without having to refetch the entire list again
 */

export function useUpdateSubtrees(
  selectedSubtree: DecisionTreeModel,
  updateSubtreeAsync: (arg: string | undefined) => Promise<DecisionTreeModel | undefined>,
  deleteSubtreeAsync: () => Promise<void>,
) {
  const queryClient = useQueryClient();

  // Creates a mutate function that updates a subtree, and if the request is successful, updates the subtree in the subtrees cache.
  const updateSubtreeMutation = useMutation(updateSubtreeAsync, {
    onSuccess: (updatedTree: DecisionTreeModel | undefined) => {
      queryClient.setQueryData(subtreeQueryKeyFactory.all, (oldData: FetchSubtreesResult | undefined) => {
        if (!oldData || !updatedTree) {
          return oldData;
        }
        // Find the subtree in oldData.subtrees and update it
        const newSubtrees = oldData.subtrees.map((tree) =>
          tree.id === selectedSubtree.id ? updatedTree : tree,
        );
        return {subtrees: newSubtrees, subtreeIds: oldData.subtreeIds};
      });
    },
  });

  // Creates a mutate function that calls deleteSubtree, and if delete is successful, removes the subtree from the subtrees cache.
  const deleteSubtreeMutation = useMutation(deleteSubtreeAsync, {
    onSuccess: () => {
      queryClient.setQueryData(subtreeQueryKeyFactory.all, (oldData: FetchSubtreesResult | undefined) => {
        if (!oldData) {
          return oldData;
        }
        // Find the deleted subtree in oldData.subtrees and remove it
        const newSubtrees = oldData.subtrees.filter((tree) => tree.id !== selectedSubtree.id);
        const newSubtreeIds = newSubtrees.map((tree) => tree.id);
        return {subtrees: newSubtrees, subtreeIds: newSubtreeIds};
      });
    },
  });

  return {updateSubtreeMutation, deleteSubtreeMutation};
}
